import { useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { IconButton } from '@components/icon-button';
import { IconDirectionLeft } from '@components/icons/direction-left';
import { IconDirectionRight } from '@components/icons/direction-right';
import { IconDownload } from '@components/icons/download';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { Modal, useModal } from '@components/modal';
import { Previewer } from '@components/previewer';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

export type PreviewFile = {
  name: string;
  src: string;
  srcConverted?: string;
};

export const usePreviewFileModal = () =>
  useModal(PreviewFileModal, { variant: 'FULLSCREEN', backgroundColor: 'backgroundPrimary' });

type PreviewFileProps = {
  formFile: PreviewFile;
  filesList?: PreviewFile[];
};

const PreviewerContainer = styled(Stack).attrs({
  align: 'center',
  justify: 'center',
})({
  marginTop: pxToRem(24),
  marginBottom: pxToRem(24),
});

const PreviewFileModal = ({ formFile, filesList = [formFile] }: PreviewFileProps) => {
  const { t } = useScopedTranslate(locale);
  const [currentIndex, setCurrentIndex] = useState(() =>
    filesList.findIndex((f) => f.src === formFile.src)
  );
  const currentFile = filesList[currentIndex];

  const traverseFiles = (direction: 'prev' | 'next') => {
    const index =
      direction === 'prev'
        ? currentIndex === 0
          ? filesList.length - 1
          : currentIndex - 1
        : (currentIndex + 1) % filesList.length;
    setCurrentIndex(index);
  };

  const headerActions = (
    <Stack orientation="horizontal" space={6}>
      <Button
        iconLeft={<IconDownload />}
        text={t('download')}
        variant="secondary"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          window.open(currentFile.src, '_blank');
        }}
      />
      {filesList.length > 1 && (
        <Stack orientation="horizontal" space={1}>
          <IconButton icon={<IconDirectionLeft />} onClick={() => traverseFiles('prev')} />
          <IconButton icon={<IconDirectionRight />} onClick={() => traverseFiles('next')} />
        </Stack>
      )}
    </Stack>
  );

  return (
    <>
      <Modal.FullscreenHeader title={currentFile.name} actions={headerActions} />
      <Modal.FullscreenContent>
        <PreviewerContainer>
          <Previewer
            url={currentFile.srcConverted || currentFile.src}
            maxWidth={1000}
            parentId={'intergrationFile-container'}
            fallback={
              <EmptyState
                contentColor="textSecondary"
                header={t('unableToPreview')}
                content={t('emptyDescription')}
                headerColor="textPrimary"
                icon={<IconVisibilityHidden size={80} />}
                orientation="vertical"
                transparent
              />
            }
          />
        </PreviewerContainer>
      </Modal.FullscreenContent>
    </>
  );
};

const locale = {
  en: {
    pdfRendered: 'PDF rendered preview',
    download: 'Download file',
    unableToPreview: 'Unable to preview document',
    emptyDescription:
      'Supported formats: pdf, doc, docx, txt, rtf.\nPlease note: you can still try downloading the file and\n opening it on your computer. ',
  },
};
